// Hook driving the Customer Merge page state and submission.
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useIntl } from "react-intl";
import { CustomerMergeSearchResult } from "@Shape-Digital/kudzu-data/lib/types/actions";
import messages from "./messages";
import {
  CustomerInMerge,
  FieldSelections,
  InitialCustomer,
  MergeError,
  MergeFieldKey,
  MergeResult,
} from "./types";
import getSelfPatient from "./getSelfPatient";
import formatSelfPatientAddress from "./formatSelfPatientAddress";
import checkCustomerHasCheckoutAppointment from "./checkCustomerHasCheckoutAppointment";
import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";

const CUSTOMER_MERGE_FIELD_KEYS: MergeFieldKey[] = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "additionalPhone",
];

const PATIENT_MERGE_FIELD_KEYS: MergeFieldKey[] = [
  "patientMrn",
  "patientDateBirth",
  "patientAddress",
  "patientRace",
  "patientEthnicity",
  "patientSex",
];

const emptySelections = (): FieldSelections =>
  Object.fromEntries(
    [...CUSTOMER_MERGE_FIELD_KEYS, ...PATIENT_MERGE_FIELD_KEYS].map((k) => [
      k,
      null,
    ]),
  ) as FieldSelections;

const getFieldValue = (
  customer: CustomerInMerge,
  field: MergeFieldKey,
): string | null => {
  switch (field) {
    case "firstName":
      return customer.firstName || null;
    case "lastName":
      return customer.lastName || null;
    case "email":
      return customer.email || null;
    case "phone":
      return customer.phone;
    case "additionalPhone":
      return customer.additionalPhone;
    case "patientMrn":
      return customer.selfPatient?.mrn ?? null;
    case "patientDateBirth":
      return customer.selfPatient?.date_birth ?? null;
    case "patientAddress":
      return formatSelfPatientAddress(customer.selfPatient);
    case "patientRace":
      return customer.selfPatient?.race ?? null;
    case "patientEthnicity":
      return customer.selfPatient?.ethnicity ?? null;
    case "patientSex":
      return customer.selfPatient?.sex ?? null;
    default:
      return null;
  }
};

const useMergeCustomers = (
  initialCustomer: InitialCustomer,
  isOpen: boolean,
) => {
  const mergeCustomers = useFirebaseAppFunction("mergeCustomers");
  const { formatMessage } = useIntl();

  const [customers, setCustomers] = useState<CustomerInMerge[]>([]);
  const [fieldSelections, setFieldSelections] = useState<FieldSelections>(
    emptySelections(),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mergeResult, setMergeResult] = useState<MergeResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<MergeError | null>(null);

  // Keep a ref so the isOpen effect can always read the latest initialCustomer
  // without adding it to the dep array (which would re-fire on every refetch).
  const initialCustomerRef = useRef(initialCustomer);
  initialCustomerRef.current = initialCustomer;

  const loadAndAddCustomer = useCallback(async (customer: InitialCustomer) => {
    const placeholder: CustomerInMerge = {
      ...customer,
      selfPatient: null,
      isLoadingSelfPatient: true,
      hasCheckoutAppointment: null,
    };
    // Dedup guard — StrictMode fires effects twice; prevents duplicate columns.
    setCustomers((prev) => {
      if (prev.some((c) => c.id === customer.id)) return prev;
      return [...prev, placeholder];
    });

    const [selfPatient, hasCheckoutAppointment] = await Promise.all([
      getSelfPatient(customer.email),
      checkCustomerHasCheckoutAppointment(customer.id),
    ]);

    setCustomers((prev) =>
      prev.map((c) =>
        c.id === customer.id
          ? {
              ...c,
              selfPatient,
              isLoadingSelfPatient: false,
              hasCheckoutAppointment,
            }
          : c,
      ),
    );
  }, []);

  // Reset state and load the initial customer once (isOpen flips true when the
  // container mounts with real customer data — see MergeCustomersScreen).
  // Using isOpen as the dep (not []) so a remount after navigating back in always starts clean.
  useEffect(() => {
    if (!isOpen) return;
    setCustomers([]);
    setFieldSelections(emptySelections());
    setMergeResult(null);
    setErrorMessage(null);
    loadAndAddCustomer(initialCustomerRef.current).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const addCustomer = useCallback(
    (result: CustomerMergeSearchResult) => {
      if (customers.some((c) => c.id === result.id)) return;
      loadAndAddCustomer({
        id: result.id,
        firstName: result.firstName,
        lastName: result.lastName,
        email: result.email,
        phone: result.phone,
        additionalPhone: result.additionalPhone,
        wards: result.wards,
        familyMembers: result.familyMembers,
      });
    },
    [customers, loadAndAddCustomer],
  );

  const removeCustomer = useCallback((customerId: string) => {
    setCustomers((prev) => prev.filter((c) => c.id !== customerId));
    setFieldSelections((prev) =>
      (Object.keys(prev) as MergeFieldKey[]).reduce(
        (acc, key) => ({
          ...acc,
          [key]: prev[key] === customerId ? null : prev[key],
        }),
        {} as FieldSelections,
      ),
    );
  }, []);

  const setFieldSelection = useCallback(
    (field: MergeFieldKey, customerId: string) => {
      setFieldSelections((prev) => ({ ...prev, [field]: customerId }));
    },
    [],
  );

  const hasAnySelfPatient = customers.some((c) => c.selfPatient !== null);

  const requiredFields: MergeFieldKey[] = hasAnySelfPatient
    ? [...CUSTOMER_MERGE_FIELD_KEYS, ...PATIENT_MERGE_FIELD_KEYS]
    : CUSTOMER_MERGE_FIELD_KEYS;

  // AC49 — mirrors mergeCustomersHandler's checkout guard, client-side.
  const blockerError: MergeError | null = useMemo(() => {
    if (customers.some((c) => c.hasCheckoutAppointment)) {
      return {
        title: formatMessage(messages.errorCheckoutAppointment),
        description: formatMessage(messages.errorCheckoutAppointmentDesc),
      };
    }
    return null;
  }, [customers, formatMessage]);

  const canSubmit =
    customers.length >= 2 &&
    !blockerError &&
    requiredFields.every((field) => {
      // AC17/AC21 — a field is only required if at least one Customer has a value for it
      const anyCustomerHasValue = customers.some(
        (c) => getFieldValue(c, field) !== null,
      );
      if (!anyCustomerHasValue) return true;

      const selectedId = fieldSelections[field];
      if (!selectedId) return false;
      const customer = customers.find((c) => c.id === selectedId);
      if (!customer) return false;
      return getFieldValue(customer, field) !== null;
    });

  // selectedCustomerId = customer whose email radio is selected (AC18)
  const selectedCustomerId = fieldSelections.email;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !selectedCustomerId) return;

    const selectedCustomer = customers.find((c) => c.id === selectedCustomerId);
    if (!selectedCustomer) return;

    const nonSelectedCustomerIds = customers
      .filter((c) => c.id !== selectedCustomerId)
      .map((c) => c.id);

    // Address wins as a whole: the four components always come from the one
    // column selected for the combined "Address" field.
    const addressOwner = fieldSelections.patientAddress
      ? customers.find((c) => c.id === fieldSelections.patientAddress)
      : null;

    const getSelectedValue = (field: MergeFieldKey) => {
      const cid = fieldSelections[field];
      const customer = cid ? customers.find((c) => c.id === cid) : null;
      return customer ? getFieldValue(customer, field) : null;
    };

    // The merged profile takes the first/last name picked in the radio
    // columns, which may come from a non-selected Customer — the success
    // alert must show that resolved name, not the selected Customer's
    // pre-merge one.
    const resolvedFirstName =
      getSelectedValue("firstName") ?? selectedCustomer.firstName;
    const resolvedLastName =
      getSelectedValue("lastName") ?? selectedCustomer.lastName;
    const resolvedName = `${resolvedFirstName} ${resolvedLastName}`;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const { data: response } = await mergeCustomers({
        selectedCustomerId,
        nonSelectedCustomerIds,
        firstName: resolvedFirstName,
        lastName: resolvedLastName,
        email: selectedCustomer.email,
        phone: getSelectedValue("phone"),
        additionalPhone: getSelectedValue("additionalPhone"),
        patientMrn: getSelectedValue("patientMrn"),
        patientDateBirth: getSelectedValue("patientDateBirth"),
        patientCity: addressOwner?.selfPatient?.city ?? null,
        patientState: addressOwner?.selfPatient?.state ?? null,
        patientPostalCode: addressOwner?.selfPatient?.postal_code ?? null,
        patientCountry: addressOwner?.selfPatient?.country ?? null,
        patientRace: getSelectedValue("patientRace"),
        patientEthnicity: getSelectedValue("patientEthnicity"),
        patientSex: getSelectedValue("patientSex"),
      });

      if (response.status === "error") {
        const errorMap: Record<string, MergeError> = {
          CHECKOUT_APPOINTMENT_EXISTS: {
            title: formatMessage(messages.errorCheckoutAppointment),
            description: formatMessage(messages.errorCheckoutAppointmentDesc),
          },
          RECORD_LOCKED: { title: formatMessage(messages.errorRecordLocked) },
          EMAIL_MISMATCH: { title: formatMessage(messages.errorEmailMismatch) },
          PATIENT_MERGE_FAILED: {
            title: formatMessage(messages.errorPatientMergeFailed),
          },
        };
        setErrorMessage(
          errorMap[response.error.code] ?? {
            title: formatMessage(messages.errorGeneric),
          },
        );
        return;
      }

      setMergeResult({
        selectedCustomerId: response.data.selectedCustomerId,
        selectedCustomerName: resolvedName,
        duplicatePatientGroups: response.data.duplicatePatientGroups,
      });
    } catch {
      setErrorMessage({
        title: formatMessage(messages.errorMergeFailed, {
          name: resolvedName,
        }),
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    canSubmit,
    customers,
    fieldSelections,
    mergeCustomers,
    selectedCustomerId,
    formatMessage,
  ]);

  const reset = useCallback(() => {
    setMergeResult(null);
    setErrorMessage(null);
    setFieldSelections(emptySelections());
  }, []);

  return {
    customers,
    fieldSelections,
    hasAnySelfPatient,
    canSubmit,
    selectedCustomerId,
    isSubmitting,
    mergeResult,
    errorMessage,
    blockerError,
    addCustomer,
    removeCustomer,
    setFieldSelection,
    handleSubmit,
    reset,
    getFieldValue,
  };
};

export default useMergeCustomers;
